import { useRef, useState, type RefObject } from 'react';
import { toBlob } from 'html-to-image';
import { BRAND, type ShareReadingRequest } from '@tarot/shared';
import { shareReading } from '../lib/api';
import { isToss, publicOrigin } from '../lib/platform';
import { isMobile, shareLink } from '../lib/share';
import { CardsIcon, ImageIcon, ShareIcon } from './icons';
import { LogoMark } from '../brand/BrandLogo';

/**
 * 운세 결과 하단 공통 액션 — [공유하기(링크)] + [다른 운세 보기] + 보조 [이미지로 저장].
 * 링크 공유: 결과를 서버에 발행(/readings/share) → /r/:shareId 링크를 OS 공유 시트/클립보드로.
 *   → 받은 사람이 링크로 서비스에 들어와 결과를 보고 "나도 보러 가기"로 유입되는 성장 루프.
 * 이미지 저장: 결과 영역 DOM 캡처(카드 덱 포함) — SNS에 이미지로 올리고 싶을 때.
 */

const CAPTURE_PIXEL_RATIO = 2;
/**
 * 사파리(특히 iOS)는 첫 캡처에서 내용 없이 배경만 그려진 이미지를 내놓는 일이 있다.
 * html-to-image는 DOM을 SVG(foreignObject)로 직렬화해 <img>로 굽는 방식인데,
 * 내부에 인라인된 이미지가 디코드되기 전에 onload가 떨어지면 빈 그림이 나온다.
 * 다시 부르면 캐시가 데워져 성공하므로, 결과를 검사해서 될 때까지 재시도한다.
 */
const CAPTURE_ATTEMPTS = 3;
/** 축소본에서 이 정도 색이 안 나오면 "배경만 있는 빈 이미지"로 본다 */
const MIN_DISTINCT_COLORS = 8;
/** 빈 이미지 판정용 축소 크기 — 원본을 그대로 훑으면 모바일에서 느리다 */
const PROBE_MAX_PX = 240;

/** 다운로드가 시작되기 전에 blob URL을 해제하면 중간에 끊는 브라우저가 있다 */
const REVOKE_DELAY_MS = 60_000;

/** blob을 파일로 내려받게 한다 (PC 공용 경로) */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // 파이어폭스는 문서에 붙어 있는 앵커만 클릭에 반응한다
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}

/** 캡처 결과에 내용이 실제로 담겼는지 검사 (배경 한 색만 있으면 실패로 본다) */
async function looksRendered(blob: Blob): Promise<boolean> {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    const scale = Math.min(1, PROBE_MAX_PX / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return true; // 검사할 수 없으면 통과시킨다 (막지 않는 쪽으로)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const colors = new Set<number>();
    for (let i = 0; i + 2 < data.length; i += 4) {
      colors.add((data[i]! << 16) | (data[i + 1]! << 8) | data[i + 2]!);
      if (colors.size >= MIN_DISTINCT_COLORS) return true;
    }
    return false;
  } catch {
    return true; // 검사 자체가 실패하면 저장을 막지 않는다
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * 캡처 바탕색 — 현재 테마의 배경 토큰을 읽는다.
 * 브랜드 색을 그대로 쓰면 라이트 테마에서 어두운 바탕에 어두운 글자가 얹혀 본문이 묻힌다
 * (실측 확인, 2026-08-20). 토큰(index.css의 --color-night)이 테마별 단일 출처다.
 */
function themeBackground(): string {
  const token = getComputedStyle(document.documentElement).getPropertyValue('--color-night').trim();
  return token || BRAND.themeColor;
}

/** 결과 영역을 이미지로. 빈 이미지가 나오면 다시 시도하고, 끝내 실패하면 예외를 던진다 */
async function captureBlob(el: HTMLElement): Promise<Blob> {
  // 웹폰트가 아직 안 붙은 상태로 구우면 글자가 밀리거나 빠진다
  await document.fonts?.ready?.catch?.(() => undefined);
  // skipFonts: 브랜드 워드마크용 구글 폰트는 교차 출처라 어차피 임베드에 실패하는데,
  //   시도하느라 1.4초를 버린다(실측). 모바일에서는 그대로 실패로 이어질 수 있다.
  // cacheBust 미사용: 이미 화면에 떠 있는 이미지를 다시 받게 만들어 빈 캡처를 유발한다.
  const opts = {
    pixelRatio: CAPTURE_PIXEL_RATIO,
    backgroundColor: themeBackground(),
    skipFonts: true,
  };
  // 첫 결과는 버린다. 사파리는 1회차에 카드 그림이 통째로 빠진 이미지를 내놓고
  // 2회차부터 담아준다 (실측: 색 2641종 → 15215종). 크로뮴은 1회차부터 정상이라
  // 이 한 번은 순전히 사파리·iOS 때문에 치르는 비용이다.
  await toBlob(el, opts);
  for (let i = 0; i < CAPTURE_ATTEMPTS; i++) {
    const blob = await toBlob(el, opts);
    if (blob && (await looksRendered(blob))) return blob;
  }
  throw new Error('capture came out blank');
}

export function ReadingActions({
  targetRef,
  shareData,
  savedShareId,
  onOther,
  otherLabel = '타로 더보기',
}: {
  targetRef: RefObject<HTMLElement | null>;
  /** 링크 공유용 결과 데이터 — 없으면 링크 공유 버튼 숨김 */
  shareData: ShareReadingRequest | null;
  /**
   * 해석 완료 시 미리 발행해 둔 shareId (usePublishReading).
   * 이게 있어야 iOS에서 공유 시트가 열린다 — 누른 뒤에 발행하면 늦다.
   */
  savedShareId?: string | null;
  onOther: () => void;
  otherLabel?: string;
}) {
  const [busy, setBusy] = useState<'link' | 'image' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * 자동으로 공유하지 못했을 때 화면에 내주는 링크.
   * 여기까지 왔다는 건 브라우저가 공유 시트도 클립보드도 막았다는 뜻이다. 그럴 때
   * "공유하지 못했어요"만 띄우면 사용자에게 남는 게 없다 — 링크를 보여주고 직접 누르게 한다.
   */
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const shareUrlRef = useRef<string | null>(null); // 같은 결과는 1회만 발행

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 2500);
  };

  /**
   * 링크를 클립보드로. 실패하면 링크를 화면에 내준다.
   * iOS는 사용자 조작 흐름을 벗어난 클립보드 쓰기도 거절하므로, 여기서 또 막히는 일이 있다.
   */
  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      flash('링크를 복사했어요 — 붙여넣어 공유하세요 🔗');
    } catch {
      setFallbackUrl(url);
    }
  };

  /**
   * ⚠️ **`navigator.share()` 앞에서 await 하지 않는다.**
   * iOS Safari는 사용자 조작과 같은 실행 흐름에서 호출된 공유만 허용한다. 발행(POST)을 먼저
   * 기다리면 그 사이 연결이 끊겨 공유 시트가 거절당한다("공유하지 못했어요"). 그래서 링크는
   * 해석이 끝나는 시점에 미리 발행해 두고(usePublishReading), 여기서는 그 값을 바로 쓴다.
   *
   * 미리 발행이 실패했거나 아직 안 끝난 경우에만 여기서 발행하는데, 그때는 공유 시트가 막힐 수
   * 있으므로 클립보드 복사로 물러난다 — 아무 일도 안 일어나는 것보다 낫다. (2026-08-21)
   */
  const onShareLink = () => {
    if (!shareData || busy) return;
    setFallbackUrl(null);
    const ready = shareUrlRef.current ?? (savedShareId && `${publicOrigin()}/r/${savedShareId}`);
    const sheet = isMobile() && typeof navigator.share === 'function';

    if (ready) {
      shareUrlRef.current = ready;
      /**
       * 미니앱에서는 **토스 공유 SDK를 거쳐야 한다** (앱인토스 안내: 공유는 공유하기 SDK로).
       * 여기만 `navigator.share`를 직접 불러서, 방 초대는 토스 링크로 나가는데 결과 공유만
       * 생 URL로 나갔다 (2026-08-28 카톡에서 확인). 같은 창구(`shareLink`)로 모은다.
       *
       * 웹은 아래 경로를 그대로 둔다 — iOS는 **클릭과 같은 실행 흐름**에서 부른 공유만
       * 허용해서, `shareLink`처럼 await가 앞에 붙으면 공유 시트가 거절당한다.
       */
      if (isToss) {
        void shareLink({
          title: BRAND.title,
          text: `내 타로 결과를 봐줘 ${BRAND.emoji}`,
          url: ready,
        }).then((r) => {
          if (r === 'copied') flash('링크를 복사했어요 — 붙여넣어 공유하세요 🔗');
          else if (r === 'failed') setFallbackUrl(ready);
        });
        return;
      }
      if (!sheet) return void copyLink(ready);
      // 여기서 await 하면 안 된다 — 클릭과 같은 흐름에서 바로 호출한다
      navigator
        .share({ title: BRAND.title, text: `내 타로 결과를 봐줘 ${BRAND.emoji}`, url: ready })
        .catch((e) => {
          if (e instanceof DOMException && e.name === 'AbortError') return; // 사용자가 닫음
          void copyLink(ready);
        });
      return;
    }

    // 아직 링크가 없다 → 발행부터. 이 경로에서는 공유 시트가 막히므로 복사·표시로 마무리한다
    setBusy('link');
    void shareReading(shareData)
      .then(({ shareId }) => {
        const url = `${publicOrigin()}/r/${shareId}`;
        shareUrlRef.current = url; // 다음 탭부터는 공유 시트가 열린다
        return copyLink(url);
      })
      .catch(() => flash('링크를 만들지 못했어요. 잠시 후 다시 눌러 주세요 😢'))
      .finally(() => setBusy(null));
  };

  const onSaveImage = async () => {
    const el = targetRef.current;
    if (!el || busy) return;
    setBusy('image');
    try {
      const blob = await captureBlob(el);
      const filename = `${BRAND.slug}-reading.png`;
      const file = new File([blob], filename, { type: 'image/png' });
      // 모바일에서만 공유 시트(사진 앱 저장·메신저 전송) — PC는 항상 브라우저 다운로드
      const canShareFile =
        isMobile() &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] });
      if (canShareFile) {
        await navigator.share({ files: [file], title: BRAND.title });
      } else {
        downloadBlob(blob, filename);
        flash('이미지를 내려받았어요 📥');
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) flash('저장하지 못했어요 😢');
    } finally {
      setBusy(null);
    }
  };

  const compact =
    'flex h-11 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-xl border border-gold/50 bg-gold/10 px-2.5 text-xs font-semibold text-gold transition hover:bg-gold/20 disabled:opacity-50';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        {/* 공유·이미지: 아이콘 + 짧은 라벨 컴팩트 버튼 */}
        <button
          onClick={onShareLink}
          disabled={busy !== null || !shareData}
          title="링크로 공유하기"
          className={compact}
        >
          <ShareIcon className="h-5 w-5 shrink-0" />
          {busy === 'link' ? '…' : '공유'}
        </button>
        <button
          onClick={onSaveImage}
          disabled={busy !== null}
          title="이미지로 저장하기"
          className={compact}
        >
          <ImageIcon className="h-5 w-5 shrink-0" />
          {busy === 'image' ? '…' : '이미지'}
        </button>
        {/* 타로 더보기: 남는 폭 전부 — 모바일에서 줄바꿈 없음 */}
        <button
          onClick={onOther}
          className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-gold px-3 text-sm font-semibold text-night transition hover:bg-gold-deep"
        >
          <CardsIcon className="h-5 w-5 shrink-0" />
          {otherLabel}
        </button>
      </div>
      {notice && <p className="text-center text-sm text-moon-dim">{notice}</p>}
      {/* 자동 공유가 막힌 경우 — 링크를 직접 내준다. 이 버튼을 누르는 건 새 사용자 조작이라
          그 흐름에서의 클립보드 쓰기는 브라우저가 허용한다 */}
      {fallbackUrl && (
        <div className="flex flex-col gap-1.5">
          <p className="text-center text-xs text-moon-dim">
            공유 창을 열지 못했어요. 링크를 복사해 보내주세요.
          </p>
          <button
            onClick={() => void copyLink(fallbackUrl)}
            className="flex items-center justify-between gap-2 rounded-xl border border-white/15 bg-night-2 px-4 py-3 text-left text-sm transition hover:border-gold/50"
          >
            <span className="truncate text-moon-dim">{fallbackUrl}</span>
            <span className="shrink-0 font-semibold text-gold">복사</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** 캡처 이미지 하단에 함께 찍히는 브랜드 서명 (결과 영역 안에 넣어서 사용) */
export function CaptureSignature() {
  return (
    <p className="flex items-center justify-center gap-1.5 pt-1 text-xs text-moon-dim/60">
      <LogoMark className="h-5 w-5 text-gold/70" />
      {BRAND.nameKo} - {BRAND.tagline}
    </p>
  );
}
